/**
 * The shell's two navigations — one set of panels, arranged for the width it is given.
 *
 * Below bp-wide the panels are a horizontally scrollable bottom bar (Hunsy has ten of them, and a
 * bar that shrank to fit ten would be untappable); at bp-wide and above the same items are a side
 * rail. That choice is made once, in App.tsx: a component that read the window width would be two
 * components, or its parent's problem.
 *
 * THE DOCK IS ITS OWN SURFACE, not the page's. `--dock` and its four companions are separate
 * tokens from `--card`/`--background` because Tuxedo's dock is midnight in BOTH modes — a brand
 * surface, like a proscenium — while Bouquet's is the booth's cream by day and hero-mid by night.
 * Nothing in here reads a page token, which is what lets one set of classes draw both.
 *
 * AND THE TAB YOU STAND ON IS A RULE, NEVER A FILL. Both sheets take that graft ("use this menu
 * with line for active tab"): a 2px rule under the label in the dock, beside it in the rail, and
 * the label lifting from `--dock-muted` to `--dock-on`. The old pill is gone deliberately — it
 * was a second filled panel competing with the header band, which is the one this round wants
 * read first.
 *
 * The badge is the pending count for that panel — the same number the nudge engine decides to
 * nudge about, so what the bar shows and what a push says can never disagree.
 */
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { MODULES, type ScreenId } from '@/registry';
import { useStore } from '@/store';
import * as React from 'react';
import { Platform, Pressable, ScrollView, View, type NativeScrollEvent, type NativeSyntheticEvent } from 'react-native';

/**
 * The pending count, drawn as whatever this look's badge is.
 *
 * Bouquet's is the booth's own solid HEART, cut with a clip-path by `.fit-badge`; Tuxedo's is a
 * square 4px chip, which is exactly what the utilities below already draw. So the flat chip is
 * both the tuxedo rendering AND the native rendering, and the heart is a web rule layered over it
 * — one element, no branch. A `clip-path` is not something a View can take and this round adds no
 * SVG-mask dependency for one 22px ornament, so a phone gets the square chip in both looks. The
 * count is the information; the shape is the room.
 */
function Count({ n, className }: { n: number; className?: string }) {
  if (!n) return null;
  return (
    <View className={cn('fit-badge bg-dock-badge h-[18px] min-w-[18px] items-center justify-center rounded-sm px-1', className)}>
      <Text className="text-dock-badge-ink text-[10px] font-bold">{n > 9 ? '9+' : n}</Text>
    </View>
  );
}

/**
 * The mark: three shapes, quiet, quiet, ember.
 *
 * The third one is `#E8A33D` as a LITERAL and never a token — it is the brand's own amber and it
 * is the same colour in both looks and both modes, which is the whole reason it is the one thing
 * on the screen that is neither red, gold, nor grey. The first two take whatever ink the surface
 * they sit on carries, which is why the dock passes `onDock`: on Tuxedo's midnight rail the page's
 * near-black foreground would draw them as three holes.
 *
 * They are round, and that survives the harder-corners graft on purpose: a bulb is a shape, not a
 * corner.
 */
export function Brand({ name, onDock }: { name?: string; onDock?: boolean }) {
  const quiet = onDock ? 'bg-dock-ink' : 'bg-foreground';
  return (
    <View className="flex-row items-center gap-2">
      <View className="flex-row items-center gap-[3px]">
        <View className={cn('h-[5px] w-[5px] rounded-full opacity-[0.35]', quiet)} />
        <View className={cn('h-[8px] w-[6.5px] rounded-full opacity-60', quiet)} />
        <View className="h-[9px] w-[8.5px] rounded-full" style={{ backgroundColor: '#E8A33D' }} />
      </View>
      <Text className={cn('font-display text-[19px] font-semibold', onDock ? 'text-dock-ink' : 'text-foreground')}>Hunsy</Text>
      {name ? (
        <Text variant="label" className={onDock ? 'text-dock-muted' : undefined}>
          {name}
        </Text>
      ) : null}
    </View>
  );
}

export function ThemeToggle({
  theme,
  onToggle,
  onDock,
  className,
}: {
  theme: string;
  onToggle: () => void;
  onDock?: boolean;
  className?: string;
}) {
  /**
   * A CONTROL, not a caption.
   *
   * It was a bare `label` — the same tracked muted capitals as "PLAYING AS GREG" an inch above it
   * — so the rail footer read as two facts rather than as a fact and a switch, and there was
   * nothing on the screen to say the second one could be pressed. It is the outline button's own
   * shape now: the 38px small height, the 10px shoulder, a hairline, and a glyph beside the word.
   *
   * Shaped like `Button variant="outline" size="sm"` rather than being one, because the rail is
   * its own surface: `--secondary` is a PAGE token and a near-white chip on Tuxedo's midnight
   * dock would be the brightest object in the shell. `onDock` picks the dock's own hairline and
   * ink instead, which is the rule the rest of this file follows.
   */
  const ink = onDock ? 'text-dock-ink' : 'text-secondary-foreground';
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
      onPress={onToggle}
      className={cn(
        'h-[38px] flex-row items-center justify-center gap-1.5 rounded-md border px-4 active:opacity-70',
        onDock ? 'border-dock-line' : 'border-secondary-line bg-secondary',
        className,
      )}>
      {/* Half-filled: the one glyph that IS the idea of two modes, and it never changes with the
          mode — a control that redraws its own icon is a control you have to read twice. */}
      <Text className={cn('text-[14px] leading-4', ink)}>◐</Text>
      {/* It reads the mode it would switch TO, which is the boards' own wording. */}
      <Text className={cn('text-small font-semibold', ink)}>{theme === 'dark' ? 'Light' : 'Dark'}</Text>
    </Pressable>
  );
}

export function SideRail({
  screen,
  onGo,
  badges,
  theme,
  onToggleTheme,
  name,
}: {
  screen: ScreenId;
  onGo: (screen: ScreenId) => void;
  badges: Record<string, number>;
  theme: string;
  onToggleTheme: () => void;
  name?: string;
}) {
  return (
    <View className="bg-dock border-dock-line w-[210px] gap-1 border-r p-3">
      <View className="px-2 pb-4">
        <Brand onDock />
      </View>

      {MODULES.map((m) => {
        const on = m.id === screen;
        return (
          <Pressable
            key={m.id}
            accessibilityRole="tab"
            accessibilityState={{ selected: on }}
            onPress={() => onGo(m.id)}
            /**
             * The dock's rule, turned on its side. No fill on the selected row, and a transparent
             * 2px border on every other one so the labels do not shuffle two pixels sideways as
             * the selection moves.
             */
            className={cn(
              'flex-row items-center gap-3 border-l-2 px-4 py-2.5',
              on ? 'border-dock-rule' : 'border-transparent active:opacity-70',
            )}>
            <Text className={cn('w-4 text-center text-[14px]', on ? 'text-dock-on' : 'text-dock-muted')}>{m.glyph}</Text>
            <Text className={cn('flex-1 text-[14px]', on ? 'text-dock-on font-bold' : 'text-dock-muted font-semibold')}>
              {m.title}
            </Text>
            <Count n={badges[m.id] ?? 0} />
          </Pressable>
        );
      })}

      <View className="flex-1" />
      <View className="border-dock-line gap-2 border-t px-2 pt-3">
        {name ? <Text variant="label" className="text-dock-muted">{`Playing as ${name}`}</Text> : null}
        <ThemeToggle theme={theme} onToggle={onToggleTheme} onDock className="self-start" />
      </View>
    </View>
  );
}

/** The narrow header: the brand, and the one control that has nowhere else to go. */
export function NarrowHeader({ theme, onToggleTheme, name }: { theme: string; onToggleTheme: () => void; name?: string }) {
  /* Tuxedo rules its top bar off the page — the FAQ page's own rule. Bouquet carries no edge here
     and lets the header float on the ground's wash. A store read rather than the CSS rule this
     used to be, so a phone gets the rule too. */
  const look = useStore((s) => s.look);
  return (
    <View className={cn('flex-row items-center gap-2 pb-3', look === 'tuxedo' && 'border-border border-b')}>
      <Brand name={name} />
      <View className="flex-1" />
      <ThemeToggle theme={theme} onToggle={onToggleTheme} />
    </View>
  );
}

/**
 * Reachable one-handed or it is not reachable. Nothing here shrinks to fit more items in.
 *
 * FIVE AND A HALF TABS, and never a round five. Ten modules in a bar that showed exactly five of
 * them, ending flush on the viewport's edge, is a bar that looks complete: the other five were
 * invisible and nothing on the screen said to push sideways. Items are sized off the measured
 * viewport so the sixth is half in view at the widths where that fits, the strip snaps so a
 * flick always lands on whole tabs rather than mid-word, and a fade sits over whichever end still
 * has something past it.
 *
 * The content container carries no horizontal padding on purpose: `snapToInterval` snaps the
 * offset to multiples of one item, so a leading inset would put every rest position a few pixels
 * off its own tab.
 *
 * The bar scrolls itself to the selected panel. A finger press keeps its own tab in view for
 * free; a `go()` from code — Make it a signal on Sync, a tapped notification — does not, and the
 * bar was left showing a stretch of tabs with none of them highlighted.
 */

/** The narrowest a tab may be cut to. Below this the label stops being a word and the glyph stops
 *  being a target, so the peek is given up rather than the tap. */
const MIN_TAB = 64;

/** How many tabs are meant to be in view: five whole ones and the promise of a sixth. */
const TABS_IN_VIEW = 5.5;

/** The width of the wash at each end, and the three steps it is built from. */
const FADE_W = 24;
const FADE_STEPS = [0.94, 0.64, 0.28];

/**
 * The "there is more this way" wash.
 *
 * Three stacked strips of the dock's own colour rather than a gradient: `expo-linear-gradient` is
 * not a dependency of this app and one 24px ornament is not a reason to make it one. Three steps
 * over eight pixels each reads as a fade at the size it is actually drawn.
 */
function DockFade({ side }: { side: 'left' | 'right' }) {
  const steps = side === 'left' ? FADE_STEPS : [...FADE_STEPS].reverse();
  return (
    <View
      pointerEvents="none"
      className={cn('absolute bottom-0 top-0 flex-row', side === 'left' ? 'left-0' : 'right-0')}
      style={{ width: FADE_W }}>
      {steps.map((opacity, i) => (
        <View key={i} className="bg-dock h-full flex-1" style={{ opacity }} />
      ))}
    </View>
  );
}

export function BottomNav({
  screen,
  onGo,
  badges,
}: {
  screen: ScreenId;
  onGo: (screen: ScreenId) => void;
  badges: Record<string, number>;
}) {
  const scroller = React.useRef<ScrollView>(null);
  const layouts = React.useRef<Partial<Record<ScreenId, { x: number; width: number }>>>({});
  const viewport = React.useRef(0);
  const current = React.useRef(screen);
  current.current = screen;

  /**
   * The tab width is STATE and the viewport is a ref, which is not an inconsistency: the width
   * changes what is drawn, and the viewport is only ever read by the scroll arithmetic below.
   */
  const [tabWidth, setTabWidth] = React.useState(0);
  const [edges, setEdges] = React.useState({ left: false, right: false });

  /**
   * Refs, not state, and called from all three places rather than only from the effect.
   *
   * onLayout fires once per element and in no guaranteed order between the ScrollView and its
   * children, so any version of this that waits for one of them in a dependency array centres the
   * bar on some mounts and not others. Whoever measures last does the scroll; by then the other
   * two facts are already in refs, so there is no stale closure to be caught by.
   */
  const centre = React.useCallback((animated: boolean) => {
    const t = layouts.current[current.current];
    if (!t || !viewport.current) return;
    const wanted = t.x + t.width / 2 - viewport.current / 2;
    /* ON THE SNAP GRID, not exactly on centre. A centred tab leaves the offset between two rest
       positions, which puts a half word at the far end — the very cut this dock was sized to stop
       — and the first flick then jumps sideways to tidy it up. Nearest whole tab is within half a
       tab of centre and always leaves whole words at both ends. */
    const grid = t.width || 1;
    scroller.current?.scrollTo({ x: Math.max(0, Math.round(wanted / grid) * grid), animated });
  }, []);

  React.useEffect(() => {
    centre(true);
  }, [screen, centre]);

  /** Which ends still have tabs past them — read off the scroll rather than counted, so it stays
   *  true at any width and after any orientation change. A pixel of slack at each end, because a
   *  snapped offset is a float and an exact comparison flickers the wash on and off. */
  const readEdges = React.useCallback((offset: number, content: number, layout: number) => {
    const left = offset > 1;
    const right = offset + layout < content - 1;
    setEdges((e) => (e.left === left && e.right === right ? e : { left, right }));
  }, []);

  const onScroll = React.useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      readEdges(contentOffset.x, contentSize.width, layoutMeasurement.width);
    },
    [readEdges],
  );

  return (
    <View className="bg-dock border-dock-line grow-0 border-t">
      <ScrollView
        ref={scroller}
        horizontal
        showsHorizontalScrollIndicator={false}
        decelerationRate="fast"
        snapToInterval={tabWidth || undefined}
        scrollEventThrottle={16}
        onScroll={onScroll}
        onContentSizeChange={(w) => readEdges(0, w, viewport.current)}
        onLayout={(e) => {
          const { width } = e.nativeEvent.layout;
          viewport.current = width;
          setTabWidth(Math.max(MIN_TAB, width / TABS_IN_VIEW));
          centre(false);
        }}
        contentContainerClassName="py-2"
        className={cn('grow-0', Platform.select({ web: '[scroll-snap-type:x_mandatory]' }))}>
        {MODULES.map((m) => {
          const on = m.id === screen;
          return (
            <Pressable
              key={m.id}
              accessibilityRole="tab"
              accessibilityState={{ selected: on }}
              onPress={() => onGo(m.id)}
              style={tabWidth ? { width: tabWidth } : undefined}
              onLayout={(e) => {
                const { x, width } = e.nativeEvent.layout;
                layouts.current[m.id] = { x, width };
                if (m.id === current.current) centre(false);
              }}
              /**
               * The 2px rule is drawn INSIDE the tab and the strip keeps its own bottom padding, or
               * the rule sits flush on the screen edge and reads as a rendering artefact rather than
               * as an underline. Unselected tabs carry the same border in transparent so the labels
               * do not lift two pixels as the selection moves.
               */
              className={cn(
                /* No horizontal padding: the width IS the tab, and every pixel of it is wanted
                   by an eight-letter label at 9px. */
                'items-center gap-1 border-b-2 pb-[10px] pt-2',
                on ? 'border-dock-rule' : 'border-transparent active:opacity-70',
                Platform.select({ web: '[scroll-snap-align:start]' }),
              )}>
              <Text className={cn('text-[17px] leading-5', on ? 'text-dock-on' : 'text-dock-muted')}>{m.glyph}</Text>
              {/* One line, always. A label that wraps at 320 makes its own tab taller than the nine
                  beside it, which is the row jumping rather than the word being long. */}
              <Text
                numberOfLines={1}
                className={cn(
                  'text-[9px] font-bold uppercase tracking-[1px]',
                  on ? 'text-dock-on' : 'text-dock-muted',
                )}>
                {m.title}
              </Text>
              <Count n={badges[m.id] ?? 0} className="absolute -top-1 right-1" />
            </Pressable>
          );
        })}
        {/**
         * HALF A TAB OF AIR AT THE END, and it is the snap grid rather than decoration.
         *
         * Ten tabs is 10 × tabWidth of content in 5.5 × tabWidth of viewport, so the furthest the
         * strip can scroll is 4.5 tabs — not a whole number of them. Every other rest position sat
         * on a tab boundary and that last one did not, which put a half word at the left end of
         * the bar exactly where the review found one. Half a tab of padding makes the maximum
         * offset five whole tabs, so both ends of the travel land on the grid.
         */}
        <View style={{ width: (tabWidth || MIN_TAB) / 2 }} />
      </ScrollView>
      {edges.left ? <DockFade side="left" /> : null}
      {edges.right ? <DockFade side="right" /> : null}
    </View>
  );
}
